import lineSplit from "@turf/line-split";
import booleanContains from "@turf/boolean-contains";
import intersect from "@turf/boolean-intersects";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import pointToLineDistance from "@turf/point-to-line-distance";
import { point as tuftPoint } from "@turf/helpers";
import turfBbox from "@turf/bbox";

export const getPolygonCoords = (polygon) => {
    return polygon.geometry.coordinates[0].map((item) => [item[1], item[0]]);
};

const clipLineByPolygon = (feature, polygon) => {
    const items = lineSplit(feature, polygon);
    if (items.features.length) {
        return items.features.filter((item) => booleanContains(polygon, item));
    } else {
        return [feature];
    }
};

export const clipByPolygon = (collection, polygon, strict) => {
    const features = [];

    collection.features.forEach((item) => {
        if (item.geometry.type !== "LineString") {
            return;
        }

        if (strict) {
            if (!intersect(item, polygon)) {
                return;
            }
        }

        const result = clipLineByPolygon(item, polygon);
        features.push(...result);
    });

    return {
        type: "FeatureCollection",
        features
    };
};

export const alphaToHex = (alpha) => {
    return Math.round(255 * alpha).toString(16);
};

export const extractOpacityFromColor = (color) => {
    const hexOpacity = color.substr(7);
    color = color.substr(0, 7);
    const opacity = 0.5; // +(parseInt(hexOpacity, 16) / 255).toFixed(2);

    return { color, opacity };
};

const snapMaximum = 50;
export const getPointForSnap = (point, lines) => {
    const distances = [];
    const result = { point: null, line: null };

    lines.forEach((item, index) => {
        const distance = pointToLineDistance(point, item, { units: "meters" });
        if (distance <= snapMaximum) {
            distances.push({ distance, index });
        }
    });

    distances.sort((a, b) => a.distance - b.distance);

    if (distances.length) {
        const line = lines[distances[0].index];
        const linePoint = nearestPointOnLine(line, point);

        result.related = line.related;
        result.index = distances[0].index;
        result.point = [linePoint.geometry.coordinates[1], linePoint.geometry.coordinates[0]];
    }

    return result;
};

export const splitFeature = (feature, point) => {
    return lineSplit(feature, tuftPoint(point));
};

export const debounce = (callback, wait) => {
    let timeoutId = null;
    return (...args) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
            callback.apply(null, args);
        }, wait);
    };
};

export const logObject = (obj) => {
    console.log(JSON.parse(JSON.stringify(obj)));
};

export const getPolygonBounds = (polygon) => {
    return turfBbox(polygon);
};

export const getPolygonBoundsWithBearing = (polygon, bearing = 0) => {
    // Получаем координаты полигона и убираем последнюю точку, если она дублирует первую
    const coordinates = polygon.geometry.coordinates[0];
    const coords = (coordinates[0][0] === coordinates[coordinates.length - 1][0] &&
                   coordinates[0][1] === coordinates[coordinates.length - 1][1])
        ? coordinates.slice(0, -1) 
        : coordinates;
    
    // Конвертируем градусы в радианы
    const angle = (-bearing * Math.PI) / 180;
    
    // Находим центроид полигона (более точный способ)
    let area = 0;
    let centerX = 0;
    let centerY = 0;
    
    for (let i = 0; i < coords.length; i++) {
        const j = (i + 1) % coords.length;
        const p1 = coords[i];
        const p2 = coords[j];
        const f = p1[0] * p2[1] - p2[0] * p1[1];
        area += f;
        centerX += (p1[0] + p2[0]) * f;
        centerY += (p1[1] + p2[1]) * f;
    }
    
    area /= 2;
    const f = 1 / (6 * area);
    centerX *= f;
    centerY *= f;
    
    // Вычисляем повернутые границы
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    
    coords.forEach(coord => {
        // Смещаем точку относительно центроида
        const dx = coord[0] - centerX;
        const dy = coord[1] - centerY;
        
        // Поворачиваем точку
        const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle) + centerX;
        const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle) + centerY;
        
        // Обновляем границы
        minX = Math.min(minX, rotatedX);
        minY = Math.min(minY, rotatedY);
        maxX = Math.max(maxX, rotatedX);
        maxY = Math.max(maxY, rotatedY);
    });
    
    // Добавляем небольшой отступ для надежности
    const padding = 0.00001; // примерно 1 метр
    return [
        minX - padding,
        minY - padding,
        maxX + padding,
        maxY + padding
    ];
};

export const getPolygonBoundsAndZoom = (polygon, map) => {
    // Получаем границы полигона через turf
    const bounds = turfBbox(polygon);
    
    // Получаем размеры контейнера карты
    const container = map.getContainer();
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    
    // Конвертируем границы в точки для расчета
    const sw = map.project([bounds[0], bounds[1]]);
    const ne = map.project([bounds[2], bounds[3]]);
    
    // Рассчитываем размеры полигона в пикселях при текущем зуме
    const polygonWidth = Math.abs(ne.x - sw.x);
    const polygonHeight = Math.abs(ne.y - sw.y);
    
    // Рассчитываем масштаб для подгонки полигона по ширине и высоте
    const scaleX = containerWidth / polygonWidth;
    const scaleY = containerHeight / polygonHeight;
    
    // Используем меньший масштаб чтобы полигон полностью поместился
    const scale = Math.min(scaleX, scaleY) * 0.85; // 0.9 для небольшого отступа
    
    // Рассчитываем новый зум
    const zoom = map.getZoom() + Math.log2(scale);
    
    // Рассчитываем центр полигона
    const center = [
        (bounds[0] + bounds[2]) / 2,
        (bounds[1] + bounds[3]) / 2
    ];
    
    return {
        center,
        zoom,
        bounds
    };
};